import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { ForbbidenComponent } from './errors/forbidden.component';
import { PageNotFoundComponent } from './errors/pageNotFound.component';
import { InternalServerErrorComponent } from './errors/internalServerError.component';
import { BadRequestComponent } from './errors/badRequest.component';
import { PasswordPolicyGuard } from './core/passwordPolicy/passwordPolicyGuard.service';
import { RolesGuard } from './core/roles/rolesGuard.service';
import { FeatureToggle, RoleTypeEnum, SoftwareOptionsForCompany } from './shared/enums';
import { AuthGuard } from './core/auth.guard';
import { RootGuard } from './core/root.guard';
import { CacheManagementGuard } from './cacheManagement/cacheManagementGuard.service';
import { ErrorsDemoGuard } from './errors/errorsDemo/errorsDemoGuard.service';
import { RegionGuard } from '@core/region/region.guard';
import { RemoteApplicationContainerComponent } from './remote-application';
import { isUrlOfStandardRemoteApplication, matchAohsRalUrl } from './remote-application/helpers';
import { SoftwareOptionOrFeatureFlagsGuard } from '@core/software-option-or-feature-flags.gurad';
import { IteroPlatformLoginGuard } from '@core/iteroPlatformLogin.guard';
import { aohsContextGuard } from './mfe-wrapper/aohs-context.guard';

const routes: Routes = [
	{
		path: '',
		canActivateChild: [AuthGuard],
		children: [
			{
				path: '',
				canActivate: [RootGuard], // guard acts as redirection service here
				children: []
			},
			{
				path: 'doctors',
				loadChildren: () => import('./doctors/doctors.module').then(m => m.DoctorsModule),
				canActivate: [RegionGuard, PasswordPolicyGuard, RolesGuard],
				data: {
					expectedRoles: [RoleTypeEnum.Doctor]
				}
			},
			{
				path: 'messages',
				loadComponent: () => import('./messages/messages.component').then(m => m.MessagesComponent),
				canActivate: [PasswordPolicyGuard, RolesGuard],
				data: { allowAnonymous: true, title: 'Messages' }
			},
			{
				path: 'notifications',
				loadChildren: () => import('./notifications/notifications.module').then(m => m.NotificationsModule)
			},
			{
				path: 'activityreport',
				loadChildren: () => import('./activityReport/activityReport.module').then(m => m.ActivityReportModule),
				canActivate: [PasswordPolicyGuard, RolesGuard],
				data: {
					expectedRoles: [
						RoleTypeEnum.Doctor,
						RoleTypeEnum.Personnel,
						RoleTypeEnum.Distributor,
						RoleTypeEnum.ManagementCompany
					]
				}
			},
			{
				path: 'rx/viewer',
				redirectTo: 'viewer-wrapper/viewer',
				pathMatch: 'prefix',
			},
			{
				path: 'viewer-wrapper',
				loadChildren: () => import('./viewer/viewer.module').then(m => m.ViewerModule),
				canActivate: [PasswordPolicyGuard, RolesGuard],
				data: {
					expectedRoles: [
						RoleTypeEnum.Doctor,
						RoleTypeEnum.Personnel,
						RoleTypeEnum.Distributor,
						RoleTypeEnum.ManagementCompany,
						RoleTypeEnum.Lab
					]
				}
			},
			{
				path: 'rx',
				loadChildren: () => import('./rx/rx.module').then(m => m.RxModule),
				canActivate: [PasswordPolicyGuard, RolesGuard],
				data: {
					expectedRoles: [
						RoleTypeEnum.Doctor,
						RoleTypeEnum.Personnel,
						RoleTypeEnum.Distributor,
						RoleTypeEnum.ManagementCompany,
						RoleTypeEnum.Lab
					]
				}
			},
			{
				path: 'personnels',
				loadChildren: () => import('./personnels/personnels.module').then(m => m.PersonnelsModule),
				canActivate: [RegionGuard, PasswordPolicyGuard, RolesGuard],
				data: {
					expectedRoles: [RoleTypeEnum.Personnel]
				}
			},
			{
				path: 'cache',
				loadChildren: () => import('./cacheManagement/cacheManagement.module').then(m => m.CacheManagementModule),
				canActivate: [PasswordPolicyGuard, RolesGuard],
				canLoad: [CacheManagementGuard],
				data: {
					expectedRoles: [
						RoleTypeEnum.Doctor,
						RoleTypeEnum.Personnel,
						RoleTypeEnum.Distributor,
						RoleTypeEnum.ManagementCompany,
						RoleTypeEnum.Lab
					]
				}
			},
			{
				path: 'errorsdemo',
				loadChildren: () => import('./errors/errorsDemo/errorsDemo.module').then(m => m.ErrorsDemoModule),
				canActivate: [PasswordPolicyGuard, RolesGuard],
				canLoad: [ErrorsDemoGuard],
				data: {
					expectedRoles: [
						RoleTypeEnum.Doctor,
						RoleTypeEnum.Personnel,
						RoleTypeEnum.Distributor,
						RoleTypeEnum.ManagementCompany,
						RoleTypeEnum.Lab
					]
				}
			},
			{
				path: 'distributors',
				loadChildren: () => import('./distributors/distributors.module').then(m => m.DistributorsModule),
				canActivate: [RegionGuard, PasswordPolicyGuard, RolesGuard],
				data: {
					expectedRoles: [RoleTypeEnum.Distributor]
				}
			},
			{
				path: 'managementcompanies',
				loadChildren: () => import('./managementCompanies/managementCompanies.module').then(m => m.ManagementCompaniesModule),
				canActivate: [RegionGuard, PasswordPolicyGuard, RolesGuard],
				data: {
					expectedRoles: [RoleTypeEnum.ManagementCompany]
				}
			},
			{
				path: 'labs',
				loadChildren: () => import('./labs/labs.module').then(m => m.LabsModule),
				canActivate: [RegionGuard, PasswordPolicyGuard, RolesGuard],
				data: {
					expectedRoles: [RoleTypeEnum.Lab],
				},
			},
			{
				path: 'practice-management',
				loadChildren: () => import('./practiceManagement/doc-practice-management/doc-practice-management.module').then(m => m.DocPracticeManagementModule),
				canActivate: [RolesGuard],
				data: {
					expectedRoles: [
						RoleTypeEnum.Doctor,
						RoleTypeEnum.Personnel,
						RoleTypeEnum.Distributor,
						RoleTypeEnum.ManagementCompany
					]
				}
			},
			{
				path: 'analytics',
				loadChildren: () => import('./shared/analytics/analytics.module').then(m => m.AnalyticsModule)
			},
			{
				path: 'mfe-wrapper',
				loadChildren: () => import('./mfe-wrapper/mfe-wrapper.module').then(m => m.MfeWrapperModule),
			},
			{
				matcher: matchAohsRalUrl,
				component: RemoteApplicationContainerComponent,
				canActivate: [SoftwareOptionOrFeatureFlagsGuard, aohsContextGuard],
				data: {
					softwareOptions: [SoftwareOptionsForCompany.AlignOralHealthSuiteClient],
					feature: FeatureToggle.OpenAohsUI,
				},
			},
			{
				matcher: isUrlOfStandardRemoteApplication(),
				component: RemoteApplicationContainerComponent,
			},
		]
	},
	{
		path: 'iosimauthresponse',
		loadChildren: () => import('./shared/iosim/iosimAuthResponse/iosimAuthResponse.module').then(m => m.IosimAuthResponseModule)
	},
	{
		path: 'passwordpolicy',
		loadChildren: () => import('./passwordPolicy/passwordPolicy.module').then(m => m.PasswordPolicyModule)
	},
	{
		path: 'recoverpassword',
		loadChildren: () => import('./recoverPassword/recoverPassword.module').then(m => m.RecoverPasswordModule)
	},
	{
		path: 'thirdpartylogin',
		loadChildren: () => import('./thirdPartyLogin/thirdPartyLogin.module').then(m => m.ThirdPartyLoginModule)
	},
	{
		path: 'logonas',
		loadChildren: () => import('./logonAs/logonAs.module').then(m => m.LogonAsModule),
		canActivate: [RolesGuard],
		data: {
			expectedRoles: [RoleTypeEnum.Internal]
		}
	},
	{ path: 'login', loadChildren: () => import('./login/landing.module').then(m => m.LandingModule), data: { isResponsive: true } },
	{ path: 'post-login', loadChildren: () => import('./login/postLogin.module').then(m => m.PostLoginModule), data: { isResponsive: true } },
	{ path: 'login-callback', loadChildren: () => import('./login/login-callback.module').then(m => m.LoginCallbackModule), canActivate: [IteroPlatformLoginGuard], data: { isResponsive: true } },
	{ path: 'login-legacy', loadChildren: () => import('./login/login.module').then(m => m.LoginModule), data: { isResponsive: true } },
	{ path: 'auth-provider-callback', loadChildren: () => import('./authProvider/authProvider.module').then(m => m.AuthProviderModule), data: { isResponsive: true } },
	{ path: 'forbidden', component: ForbbidenComponent, pathMatch: 'full', data: { title: '403' } },
	{ path: 'pagenotfound', component: PageNotFoundComponent, pathMatch: 'full', data: { title: '404' } },
	{ path: 'internalservererror', component: InternalServerErrorComponent, pathMatch: 'full', data: { title: '500' } },
	{ path: 'badrequest', component: BadRequestComponent, pathMatch: 'full', data: { title: '400' } },
	{ path: '**', component: PageNotFoundComponent, data: { title: '404' } }
];

@NgModule({
	imports: [RouterModule.forRoot(routes, {})],
	exports: [RouterModule]
})
export class AppRoutes { }
